/**
 * Routes du portail étudiant (PWA).
 * Toutes préfixées par /portail.
 */
import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PortailLogin from './pages/Login';
import FindMatricule from './pages/FindMatricule';
import PremiereConnexion from './pages/PremiereConnexion';
import Accueil from './pages/Accueil';
import Menu from './pages/Menu';
import Releve from './pages/Releve';
import MesReclamations from './pages/MesReclamations';
import Verifier from './pages/Verifier';
import Historique from './pages/Historique';
import Contact from './pages/Contact';
import Maintenance from './pages/Maintenance';
import NotificationsPage from './pages/Notifications';
import MesDemandesActivation from './pages/MesDemandesActivation';
import MesMessagesScolarite from './pages/MesMessagesScolarite';
import EmploisTempsExamens from './pages/EmploisTempsExamens';
import EmploiTempsCours from './pages/EmploiTempsCours';
import Suggestion from './pages/Suggestion';
import MessagesFormateur from './pages/MessagesFormateur';
import MesCours from './pages/MesCours';
import BanqueSujets from './pages/BanqueSujets';
import MaSalleExamen from './pages/MaSalleExamen';
import MessagesCamarades from './pages/MessagesCamarades';
import Messagerie from './pages/Messagerie';
import Actualites from './pages/Actualites';
import EnLigne from './pages/EnLigne';
import Reglages from './pages/Reglages';
import AnniversairesIndex, { AnniversaireDetail } from './pages/Anniversaires';
import Entraide from './pages/Entraide';
import { portailApi } from './services/portailApi';
import MessagerieFloating from '../components/Messagerie/MessagerieFloating';
import PortailChatFAB from './components/PortailChatFAB';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';

const PortailProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = portailApi.getToken();
  if (!token) {
    return <Navigate to="/portail/login" replace />;
  }
  return <>{children}</>;
};

/** Injecte <link rel="manifest"> dans le <head> uniquement quand on est sur /portail/*.
 * Le fichier manifest.json a `start_url: /portail/login` et `scope: /portail/`,
 * empêchant ainsi l'installation de la PWA depuis le backoffice.
 *
 * NB : on nettoie AUSSI les autres manifests concurrents (formateur, surveillant)
 * pour supporter la navigation `/formateur → /portail` dans le même onglet ;
 * sans ça, le navigateur garde l'ancien manifest et refuse l'install du portail. */
const useManifestPortail = () => {
  useEffect(() => {
    document.querySelectorAll('link[rel="manifest"]').forEach((el) => {
      if ((el as HTMLLinkElement).id !== 'portail-manifest') {
        el.parentNode?.removeChild(el);
      }
    });
    if (document.getElementById('portail-manifest')) return;
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/manifest.json';
    link.id = 'portail-manifest';
    document.head.appendChild(link);
    // NB : ne pas retirer au démontage — l'utilisateur peut naviguer entre
    // pages du portail sans casser l'installabilité.
  }, []);
};

/** Enregistre le service worker `/sw.js` dès l'arrivée sur /portail/*.
 *
 * Sans SW enregistré (avec un fetch handler), Chrome/Edge n'émettent PAS
 * l'événement `beforeinstallprompt` et l'app n'apparaît pas comme installable.
 * Avant ce fix, le SW n'était enregistré qu'après login (via usePushNotifications
 * dans Accueil/Notifications) — donc la bannière d'installation ne montrait
 * jamais sur la page de login, qui est le point d'entrée typique. */
const useServiceWorkerPortail = () => {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').catch(() => { /* silencieux */ });
  }, []);
};

/**
 * Redirect qui PRÉSERVE la query string d'origine (au contraire de <Navigate>
 * qui la perd). Fix 2026-08-29 : les clics "Ouvrir chat" du bouton En Ligne
 * naviguaient vers /portail/messages-camarades?conv=594, mais le
 * <Navigate to="/portail/messagerie?tab=camarades" replace /> perdait ?conv=594
 * → la conversation ne s'ouvrait jamais.
 *
 * Fusionne :
 *   - la query string existante (ex : ?conv=594 conservée)
 *   - les params `mergeQuery` (ex : {tab: 'camarades'} ajouté/écrasé)
 */
const RedirectPreserveQuery: React.FC<{ to: string; mergeQuery?: Record<string, string> }> = ({
  to, mergeQuery,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (mergeQuery) {
    for (const [k, v] of Object.entries(mergeQuery)) params.set(k, v);
  }
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
};

export default function PortailRoutes() {
  useManifestPortail();
  useServiceWorkerPortail();
  return (
    <>
    <Routes>
      <Route path="login" element={<PortailLogin />} />
      <Route path="chercher-matricule" element={<FindMatricule />} />
      <Route path="premiere-connexion" element={<PremiereConnexion />} />
      <Route path="mot-de-passe-oublie" element={<PremiereConnexion reset />} />
      <Route path="accueil" element={<PortailProtected><Accueil /></PortailProtected>} />
      <Route path="menu" element={<PortailProtected><Menu /></PortailProtected>} />
      <Route path="releve" element={<PortailProtected><Releve /></PortailProtected>} />
      <Route path="mes-reclamations" element={<PortailProtected><MesReclamations /></PortailProtected>} />
      <Route path="notifications" element={<PortailProtected><NotificationsPage /></PortailProtected>} />
      <Route path="mes-demandes" element={<PortailProtected><MesDemandesActivation /></PortailProtected>} />
      {/* Hub unique messagerie : camarades + scolarité */}
      <Route path="messagerie" element={<PortailProtected><Messagerie /></PortailProtected>} />
      {/* Anciennes routes conservées (compat liens push existants) */}
      <Route path="messages" element={<RedirectPreserveQuery to="/portail/messagerie" mergeQuery={{ tab: 'scolarite' }} />} />
      <Route path="messages/:id" element={<PortailProtected><MesMessagesScolarite /></PortailProtected>} />
      <Route path="messages-camarades" element={<RedirectPreserveQuery to="/portail/messagerie" mergeQuery={{ tab: 'camarades' }} />} />
      <Route path="emplois-temps" element={<PortailProtected><EmploisTempsExamens /></PortailProtected>} />
      <Route path="emploi-temps-cours" element={<PortailProtected><EmploiTempsCours /></PortailProtected>} />
      <Route path="suggestion" element={<PortailProtected><Suggestion /></PortailProtected>} />
      <Route path="messages-formateur" element={<PortailProtected><MessagesFormateur /></PortailProtected>} />
      <Route path="mes-cours" element={<PortailProtected><MesCours /></PortailProtected>} />
      <Route path="banque-sujets" element={<PortailProtected><BanqueSujets /></PortailProtected>} />
      <Route path="ma-salle" element={<PortailProtected><MaSalleExamen /></PortailProtected>} />
      <Route path="actualites" element={<PortailProtected><Actualites /></PortailProtected>} />
      <Route path="en-ligne" element={<PortailProtected><EnLigne /></PortailProtected>} />
      <Route path="reglages" element={<PortailProtected><Reglages /></PortailProtected>} />
      <Route path="anniversaires" element={<PortailProtected><AnniversairesIndex /></PortailProtected>} />
      <Route path="anniversaires/:matricule" element={<PortailProtected><AnniversaireDetail /></PortailProtected>} />
      <Route path="entraide" element={<PortailProtected><Entraide /></PortailProtected>} />
      <Route path="maintenance" element={<Maintenance />} />
      <Route path="verifier/:code" element={<Verifier />} />
      <Route path="historique" element={<PortailProtected><Historique /></PortailProtected>} />
      <Route path="contact" element={<PortailProtected><Contact /></PortailProtected>} />
      {/* Fix 2026-09-02 : chemin ABSOLU obligatoire. `to="login"` (relatif)
          crée une boucle sur toute URL /portail/quelque-chose/ hors routes
          définies : /portail/recouvrement/ → /portail/recouvrement/login →
          catch-all → /portail/recouvrement/login/login → boucle infinie. */}
      <Route path="*" element={<Navigate to="/portail/login" replace />} />
    </Routes>
    {/* Widget flottant messagerie — monté uniquement quand l'étudiant est
        authentifié (route protégée), pour éviter tout ping 401 sur /portail/login. */}
    <MessagerieFloatingPortail />
    {/* Détecteur nouvelle version PWA : compare le hash du bundle chargé vs
        celui référencé dans /index.html distant. Si différent → banner recharger. */}
    <UpdateAvailableBanner />
    </>
  );
}

/** Bouton flottant chat portail étudiant (fix 2026-08-31).
 *  Remplace <MessagerieFloating> (composant backoffice) qui utilisait
 *  les endpoints `/api/messagerie-chat/*` (403 pour étudiant) et un
 *  Redux ChatDrawersStack absent du portail → cliquer ne faisait rien.
 *  Le nouveau composant utilise `/api/portail/me/en-ligne/` + navigate. */
const MessagerieFloatingPortail: React.FC = () => {
  return <PortailChatFAB />;
};
